import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

/**
 * Hook para coleta automática de métricas de uso
 * Coleta métricas diariamente e atualiza os contadores de licença
 */
export const useMetricsCollector = () => {
  const { toast } = useToast();
  const intervalRef = useRef<NodeJS.Timeout>();

  const collectMetricsForAllClients = async () => {
    try {
      console.log('Starting metrics collection...');

      // Buscar todos os clientes ativos
      const { data: clients, error: clientsError } = await supabase
        .from('clients')
        .select('id, name')
        .eq('status', 'active');

      if (clientsError) throw clientsError;

      const today = new Date().toISOString().split('T')[0];
      const collectPromises = clients.map(async (client) => {
        try {
          // Contar usuários atuais por tipo
          const { data: users, error: usersError } = await supabase
            .from('users')
            .select('role, updated_at, status')
            .eq('client_id', client.id);

          if (usersError) throw usersError;

          const activeUsers = users?.filter(u => u.status === 'active') || [];
          const totalTechnicians = activeUsers.filter(u => u.role === 'technician').length;
          const totalUsers = activeUsers.filter(u => ['user', 'client_admin'].includes(u.role)).length;

          // Contar usuários ativos nos últimos 30 dias
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

          const activeTechnicians = activeUsers.filter(u => 
            u.role === 'technician' && 
            new Date(u.updated_at) >= thirtyDaysAgo
          ).length;

          const activeUserCount = activeUsers.filter(u => 
            ['user', 'client_admin'].includes(u.role) && 
            new Date(u.updated_at) >= thirtyDaysAgo
          ).length;

          // Contar tickets criados e resolvidos hoje
          const { data: ticketsCreated } = await supabase
            .from('incidentes')
            .select('id')
            .eq('client_id', client.id)
            .gte('criado_em', `${today}T00:00:00Z`)
            .lt('criado_em', `${today}T23:59:59Z`);

          const { data: ticketsResolved } = await supabase
            .from('incidentes')
            .select('id')
            .eq('client_id', client.id)
            .eq('status', 'resolvida')
            .gte('atualizado_em', `${today}T00:00:00Z`)
            .lt('atualizado_em', `${today}T23:59:59Z`);

          // Inserir/atualizar métricas
          const metricsData = {
            client_id: client.id,
            metric_date: today,
            total_technicians: totalTechnicians,
            total_users: totalUsers,
            active_technicians: activeTechnicians,
            active_users: activeUserCount,
            tickets_created: ticketsCreated?.length || 0,
            tickets_resolved: ticketsResolved?.length || 0,
            storage_used_mb: 0 // TODO: Implementar cálculo real de storage
          };

          const { error: metricsError } = await supabase
            .from('client_usage_metrics')
            .upsert(metricsData, {
              onConflict: 'client_id,metric_date'
            });

          if (metricsError) throw metricsError;

          // Atualizar contadores na licença do cliente
          const { error: licenseError } = await supabase
            .from('client_licenses')
            .update({
              current_technicians: totalTechnicians,
              current_users: totalUsers
            })
            .eq('client_id', client.id)
            .eq('status', 'active');

          if (licenseError) throw licenseError;

          return { client: client.name, success: true };
        } catch (error) {
          console.error(`Error collecting metrics for client ${client.name}:`, error);
          return { client: client.name, success: false, error };
        }
      });

      const results = await Promise.all(collectPromises);
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;

      console.log(`Metrics collection completed: ${successful} successful, ${failed} failed`);

      if (failed > 0) {
        console.warn('Some metrics collection failed:', results.filter(r => !r.success));
      }

      return { successful, failed, total: clients.length };
    } catch (error) {
      console.error('Error in metrics collection:', error);
      throw error;
    }
  };

  const startAutomaticCollection = () => {
    // Executar a cada 6 horas (6 * 60 * 60 * 1000)
    const COLLECTION_INTERVAL = 6 * 60 * 60 * 1000;

    intervalRef.current = setInterval(async () => {
      try {
        await collectMetricsForAllClients();
      } catch (error) {
        console.error('Automatic metrics collection failed:', error);
      }
    }, COLLECTION_INTERVAL);

    // Executar uma primeira coleta imediatamente
    collectMetricsForAllClients().catch(error => {
      console.error('Initial metrics collection failed:', error);
    });
  };

  const stopAutomaticCollection = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
  };

  // Iniciar coleta automática quando o hook é montado
  useEffect(() => {
    startAutomaticCollection();

    // Limpar interval quando o componente desmonta
    return () => {
      stopAutomaticCollection();
    };
  }, []);

  return {
    collectMetricsForAllClients,
    startAutomaticCollection,
    stopAutomaticCollection
  };
};